/**
 * Component: Reveal Animations
 * Description:
 *   Animates elements into view when they enter the viewport
 *   using Intersection Observer. Supports single-block reveals
 *   and staggered child reveals.
 *
 * Usage:
 *   - Pass a NodeList or array of elements with the `.reveal` class
 *   - Automatically initialises observers and applies visibility state
 *
 * Notes:
 *   - Elements are animated once by default
 *   - Accessibility-safe (no visibility/display toggling)
 */

export function revealAnimations(elements) {
    if (!elements || !elements.length) return;

    const revealElements = Array.from(elements);
    const visibleClass = 'reveal--visible';

    const observerOptions = {
        threshold: 0.15
    };

    const observer = new IntersectionObserver(onIntersection, observerOptions);

    /**
     * Intersection callback
     */
    function onIntersection(entries) {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;

            reveal(entry.target);
            observer.unobserve(entry.target);
        });
    }

    /**
     * Reveal a single element
     */
    function reveal(element) {
        element.classList.add(visibleClass);
    }

    /**
     * Assign stagger indexes to child elements
     */
    function setupStagger(element) {
        const children = Array.from(element.children);

        children.forEach((child, index) => {
            child.style.setProperty('--reveal-index', index);
        });
    }

    /**
     * Initialise each reveal element
     */
    revealElements.forEach((element) => {
        if (element.classList.contains('reveal--stagger')) {
            setupStagger(element);
        }

        observer.observe(element);
    });
}
